import { isArchivedMember, type ActorDTO } from "@roomate/core";

export { isArchivedMember };

/** People in the room. Removed members stay out even if a row is still marked active. */
export function teamPeople<T extends Pick<ActorDTO, "kind" | "active" | "archived">>(actors: readonly T[]): T[] {
  return actors.filter((actor) => actor.kind === "human" && actor.active && !isArchivedMember(actor));
}

/** Admin restore list. Not the room. */
export function removedMembers<T extends Pick<ActorDTO, "kind" | "archived">>(actors: readonly T[]): T[] {
  return actors.filter((actor) => isArchivedMember(actor));
}

/** Desks in the office. */
export function officeActors<T extends Pick<ActorDTO, "kind" | "active" | "archived">>(actors: readonly T[]): T[] {
  return actors.filter((actor) => actor.active && !isArchivedMember(actor));
}

/** Builder picker. Review-only people and removed members are not choices. */
export function builderOptions<T extends Pick<ActorDTO, "kind" | "active" | "archived" | "access" | "canBuild">>(actors: readonly T[]): T[] {
  return actors.filter((actor) => actor.active && !isArchivedMember(actor) && actor.access !== "viewer" && !(actor.kind === "human" && actor.canBuild === false));
}

/** Human reviewer picker. */
export function reviewerOptions<T extends Pick<ActorDTO, "id" | "kind" | "active" | "archived">>(actors: readonly T[], builderId?: string): T[] {
  return actors.filter((actor) => actor.kind === "human" && actor.active && !isArchivedMember(actor) && actor.id !== builderId);
}

/** Person filter and other mention targets. Existing threads still resolve names from the full list. */
export function mentionPeople<T extends Pick<ActorDTO, "kind" | "archived">>(actors: readonly T[]): T[] {
  return actors.filter((actor) => actor.kind === "human" && !isArchivedMember(actor));
}

/** Command palette and anyone offered as a current person on the Bridge. */
export function paletteActors<T extends Pick<ActorDTO, "kind" | "archived">>(actors: readonly T[]): T[] {
  return actors.filter((actor) => !isArchivedMember(actor));
}

/** Presence rail on the Bridge and the rest of the shell. */
export function presenceHumans<T extends Pick<ActorDTO, "id" | "kind" | "archived">>(actors: readonly T[], meId: string, presentIds: ReadonlySet<string>): T[] {
  return actors.filter((actor) => actor.kind === "human" && !isArchivedMember(actor) && (actor.id === meId || presentIds.has(actor.id)));
}

/** Name for a past decision, review, provenance row or task comment. Removed people keep their name. */
export function historyName(actors: readonly { id: string; name: string }[] | undefined, id: string | null | undefined): string {
  if (!id) return "";
  return actors?.find((actor) => actor.id === id)?.name ?? id;
}
